// contexts/OnboardingContext.jsx
// Client-side analog of the mobile OnboardingProvider. Holds an in-progress
// UserProfile object, merges updates (updateField), and persists to
// users/{uid} via Firestore with merge:true — matching the mobile toMap() shape.
import { createContext, useCallback, useContext, useMemo, useState } from "react"
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../firebaseConfig"

const OnboardingContext = createContext(null)

// Build the Firestore payload — mirrors UserProfile.toMap() from the mobile app.
// Only non-empty string fields are included; address/desiredRoles only when populated.
function buildPayload(p) {
  if (!p) return {}
  const map = {
    uid: p.uid,
    onboardingComplete: !!p.onboardingComplete,
  }
  if (p.createdAt) map.createdAt = p.createdAt
  if (Array.isArray(p.desiredRoles) && p.desiredRoles.length > 0) {
    map.desiredRoles = p.desiredRoles
  }
  if (p.address && Object.keys(p.address).length > 0) {
    map.address = p.address
  }
  const add = (key, value) => {
    if (value != null && String(value).trim() !== "") map[key] = value
  }
  add("firstName",      p.firstName)
  add("lastName",       p.lastName)
  add("name",           p.name)
  add("email",          p.email)
  add("phone",          p.phone)
  add("countryCode",    p.countryCode)
  add("dateOfBirth",    p.dateOfBirth)
  add("profileUrl",     p.profileUrl)
  add("currRole",       p.currRole)
  add("currCompany",    p.currCompany)
  add("currSalary",     p.currSalary)
  add("expSalary",      p.expSalary)
  add("resumeUrl",      p.resumeUrl)
  add("candidateType",  p.candidateType)
  add("gender",         p.gender)
  add("noticePeriod",   p.noticePeriod)
  add("linkedinUrl",    p.linkedinUrl)
  add("githubUrl",      p.githubUrl)
  add("websiteUrl",     p.websiteUrl)
  add("howHeard",       p.howHeard)
  add("jobSearchStatus",p.jobSearchStatus)
  return map
}

export function OnboardingProvider({ children }) {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  const initProfile = useCallback((uid, email) => {
    setProfile({
      uid,
      email: email || null,
      address: {},
      desiredRoles: [],
      onboardingComplete: false,
      createdAt: new Date().toISOString(),
    })
  }, [])

  const updateField = useCallback((patch) => {
    setProfile((prev) => {
      if (!prev) return prev
      const next = { ...prev }
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) continue
        if (k === "address") {
          next.address = { ...(next.address || {}), ...(v || {}) }
        } else {
          next[k] = v
        }
      }
      return next
    })
  }, [])

  const saveProfile = useCallback(async () => {
    if (!profile?.uid) return
    setLoading(true); setError(null)
    try {
      await setDoc(doc(db, "users", profile.uid), buildPayload(profile), { merge: true })
    } catch (e) {
      setError(e?.message || String(e))
      throw e
    } finally {
      setLoading(false)
    }
  }, [profile])

  // Final step — mark complete, register proxy mailbox, persist.
  const completeOnboarding = useCallback(async () => {
    if (!profile?.uid) return
    const email = profile.email || ""
    const uid   = profile.uid
    const at    = email.indexOf("@")
    if (email && uid && at > 0) {
      const local = email.substring(0, at).toLowerCase()
      try {
        await setDoc(
          doc(db, "proxy_mailbox", local),
          { email, userId: uid },
          { merge: true },
        )
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[proxy_mailbox] register failed:", e)
        }
      }
    }
    const final = { ...profile, onboardingComplete: true }
    setProfile(final)
    setLoading(true); setError(null)
    try {
      await setDoc(doc(db, "users", uid), buildPayload(final), { merge: true })
    } catch (e) {
      setError(e?.message || String(e))
      throw e
    } finally {
      setLoading(false)
    }
  }, [profile])

  const loadProfile = useCallback(async (uid) => {
    setLoading(true); setError(null)
    try {
      const snap = await getDoc(doc(db, "users", uid))
      if (!snap.exists()) {
        setProfile(null)
        return null
      }
      const data = snap.data() || {}
      const loaded = {
        uid,
        firstName:          data.firstName          ?? null,
        lastName:           data.lastName           ?? null,
        name:               data.name               ?? null,
        email:              data.email              ?? null,
        phone:              data.phone              ?? null,
        countryCode:        data.countryCode        ?? "+91",
        dateOfBirth:        data.dateOfBirth        ?? null,
        profileUrl:         data.profileUrl         ?? null,
        currRole:           data.currRole           ?? null,
        currCompany:        data.currCompany        ?? null,
        currSalary:         data.currSalary != null ? String(data.currSalary) : null,
        expSalary:          data.expSalary  != null ? String(data.expSalary)  : null,
        resumeUrl:          data.resumeUrl          ?? null,
        candidateType:      data.candidateType      ?? null,
        gender:             data.gender             ?? null,
        noticePeriod:       data.noticePeriod       ?? null,
        linkedinUrl:        data.linkedinUrl        ?? null,
        githubUrl:          data.githubUrl          ?? null,
        websiteUrl:         data.websiteUrl         ?? null,
        howHeard:           data.howHeard           ?? null,
        jobSearchStatus:    data.jobSearchStatus    ?? null,
        address:            (data.address && typeof data.address === "object") ? { ...data.address } : {},
        desiredRoles:       Array.isArray(data.desiredRoles) ? data.desiredRoles : [],
        onboardingComplete: data.onboardingComplete === true,
        createdAt:          data.createdAt ?? null,
      }
      setProfile(loaded)
      return loaded
    } catch (e) {
      setError(e?.message || String(e))
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const value = useMemo(() => ({
    profile,
    loading,
    error,
    initProfile,
    updateField,
    saveProfile,
    completeOnboarding,
    loadProfile,
  }), [profile, loading, error, initProfile, updateField, saveProfile, completeOnboarding, loadProfile])

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error("useOnboarding must be used within OnboardingProvider")
  return ctx
}

// Server timestamp alias, in case a caller wants to switch away from ISO strings.
export { serverTimestamp }
